// MACHINERY V2 (Stroje) — Diely a materiál: the DESKTOP installed-stock row
// (M2.5).
//
// SCOPE NARROWED AT M2.16d (step 3, near-cap split). This file used to carry
// the phone card and the shared row contract too. They now live in
// PartPhoneCard.tsx and partsRowContract.ts; what is left is the >= 768 grid
// row and nothing else. Same markup, same call site, smaller file.
//
// Spec: data package §5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-parts.jsx (`PcHead`, `PcRow`);
// artboards 1009 (desktop) and 1013 (phone).
//
// FIELDS THAT ARE FETCHED AND NOT SHOWN STAY FETCHED. §5 lists price, supplier,
// description, dimensions, condition and availability as "present, never shown".
// The row simply does not read them; the DTO is never trimmed, because the whole
// `InstalledStockItemStatsViewModel` is what the endpoint returns and what the
// slice stores. Do not "optimise" the fetch.
//
// IDENTICAL FOR CATALOG AND CUSTOM MACHINES. §5 catalog-vs-custom: the component
// takes only `machineId` and has no field divergence. There is no gate here and
// there must not be one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import FleetCardMenu from "../../fleet/FleetCardMenu";
import { InstalledOverStock, PartCheckbox, PartThumb } from "./partsAtoms";
import { PC_GRID, PartRowProps, partNumber } from "./partsRowContract";

export const PartRow: React.FC<PartRowProps> = ({
  item,
  selected,
  onToggleSelect,
  actions,
  onMarketplace,
  menuLabel,
  selectLabel,
  // FORWARDED, NOT ABSORBED (M2.16d review, F5). This destructure used to stop
  // at `selectLabel`, so the prop PartsTabV2 passes for exactly this row was
  // dropped on the floor: at 768-1279 — the tier the prop was added for — the
  // select-all box in PartsHeadRow rendered 44x44 while every row beneath it
  // rendered a 16px box in the SAME grid column.
  touch,
}) => {
  const { translate } = useTranslation("myMachinery");
  const stock = item.stockItem;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: PC_GRID,
        gap: 12,
        padding: "9px 14px",
        alignItems: "center",
        borderTop: "1px solid var(--border-soft)",
        background: selected ? "var(--mod-accent-soft)" : "transparent",
        minWidth: 0,
      }}
    >
      <PartCheckbox
        checked={selected}
        label={selectLabel}
        onChange={onToggleSelect}
        touch={touch}
      />
      <PartThumb src={stock?.file?.content} />
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-mono)",
            fontSize: 12.5,
            fontWeight: 700,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={partNumber(item)}
        >
          {partNumber(item)}
        </span>
        {stock?.alternativeNumber && (
          <span
            style={{
              display: "block",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--faint)",
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {stock.alternativeNumber}
          </span>
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--ink-2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {stock?.product?.manufacturer?.name}
        </span>
        <span
          style={{
            display: "block",
            fontSize: 11.5,
            color: "var(--muted)",
            marginTop: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {stock?.product?.category?.name}
        </span>
      </span>
      <InstalledOverStock installed={item.quantity} inStock={item.inStockQuantity} />
      <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onMarketplace}
          style={{
            color: "var(--mod-accent)",
            fontWeight: 600,
            height: 26,
            padding: "0 9px",
            whiteSpace: "nowrap",
          }}
        >
          {translate("myMachinery:v2.tabs.parts.marketplace")}
        </button>
        <FleetCardMenu actions={actions} ariaLabel={menuLabel} />
      </span>
    </div>
  );
};
